// src/views/examples/AdminBranch.jsx
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Card,
  CardHeader,
  CardFooter,
  Table,
  Container,
  Row,
  Button,
  CardBody,
  Pagination,
  PaginationItem,
  PaginationLink,
  Col,
  Input,
} from "reactstrap";
import Header from "components/Headers/Header";
import { useSessionStore } from "../../stores/sessionStore";
import { useTablePaging, PAGE_SIZE_OPTIONS } from "../../stores/pagingStore";
import { fetchBranch } from "../../api/branch";
import LoadingSpinner from "components/LoadingSpinner";

const TABLE_KEY = "branch-list";

const AdminBranch = () => {
  const navigate = useNavigate();
  const orgId = useSessionStore((s) => s.profile?.orgId);

  const { page, pageSize, setPage, setPageSize } = useTablePaging(
    TABLE_KEY,
    10,
  );

  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);

  const [loading, setLoading] = useState(false);
  const [keyword, setKeyword] = useState("");

  // ✅ responsive: ถ้าจอเล็ก ใช้ table เดิม / ถ้าจอกลาง-ใหญ่ ใช้ table แบบ Product
  const [isSmall, setIsSmall] = useState(window.innerWidth < 768);
  useEffect(() => {
    const onResize = () => setIsSmall(window.innerWidth < 768);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const totalPages = useMemo(
    () => Math.max(1, Math.ceil(Number(total || 0) / Number(pageSize || 10))),
    [total, pageSize],
  );

  useEffect(() => {
    if (!orgId) return;

    (async () => {
      setLoading(true);
      try {
        const { rows, total } = await fetchBranch({
          orgId,
          pageIndex: page - 1, // page ใน store เริ่ม 1
          pageSize,
        });
        setRows(Array.isArray(rows) ? rows : []);
        setTotal(Number(total || 0));
      } catch (e) {
        console.error("fetchBranch error:", e);
        setRows([]);
        setTotal(0);
      } finally {
        setLoading(false);
      }
    })();
  }, [orgId, page, pageSize]);

  // ✅ กรอง “เฉพาะข้อมูลในหน้าปัจจุบัน” ตาม keyword (เหมือนโค้ดเดิม)
  const displayRows = useMemo(() => {
    const k = keyword.trim().toLowerCase();
    if (!k) return rows;

    return rows.filter((r) => {
      const code = (r.brCode || "").toLowerCase();
      const name = (r.brNameTH || "").toLowerCase();
      const mobile = (r.brMobile || "").toLowerCase();
      const email = (r.brEmail || "").toLowerCase();
      return (
        code.includes(k) ||
        name.includes(k) ||
        mobile.includes(k) ||
        email.includes(k)
      );
    });
  }, [rows, keyword]);

  // ✅ ใช้ total จาก API เป็นจำนวนทั้งหมด
  const filteredTotal = total;

  const pageItems = useMemo(() => {
    const items = [];
    const maxAround = 2;
    const start = Math.max(1, page - maxAround);
    const end = Math.min(totalPages, page + maxAround);
    for (let p = start; p <= end; p++) items.push(p);
    return items;
  }, [page, totalPages]);

  if (loading) {
    return <LoadingSpinner fullPage />;
  }

  return (
    <>
      <Header />
      <Container className="mt--7" fluid>
        <Row>
          <Col xs="12">
            <Card className="bg-secondary shadow">
              <CardHeader className="border-0 d-flex align-items-center justify-content-between">
                <h6 className="mb-0 ml-3">ข้อมูลสาขา</h6>
                <Button color="success" onClick={() => navigate("/add-branch")}>
                  เพิ่มข้อมูลสาขา
                </Button>
              </CardHeader>

              <CardBody>
                {/* Toolbar */}
                <div className="mb-3">
                  <Row className="align-items-center">
                    <Col
                      xs="12"
                      md="6"
                      className="d-flex align-items-center"
                      style={{ gap: 8 }}
                    >
                      <Input
                        type="select"
                        value={pageSize}
                        onChange={(e) =>
                          setPageSize(TABLE_KEY, Number(e.target.value))
                        }
                        style={{ width: 120 }}
                      >
                        {PAGE_SIZE_OPTIONS.map((n) => (
                          <option key={n} value={n}>
                            {n}
                          </option>
                        ))}
                      </Input>
                      <span className="text-muted">แถว/หน้า</span>
                    </Col>
                    <Col
                      xs="12"
                      md="6"
                      className="mt-2 mt-md-0 d-flex justify-content-md-end"
                    >
                      <Input
                        type="text"
                        placeholder="ค้นหา รหัสสาขา/ชื่อสาขา/เบอร์โทร"
                        value={keyword}
                        onChange={(e) => setKeyword(e.target.value)}
                        style={{ maxWidth: 320, minWidth: 260 }}
                      />
                    </Col>
                  </Row>
                </div>

                {/* ✅ TABLE: เล็ก=เดิม, กลาง/ใหญ่=Product style */}
                {isSmall ? (
                  <div className="table-responsive">
                    <Table hover className="table table-modern mb-0">
                      <thead className="thead-colored">
                        <tr>
                          <th className="text-start">รหัสสาขา</th>
                          <th className="text-start">ชื่อสาขา</th>
                          <th className="text-start">เบอร์โทร</th>
                          <th className="text-start">อีเมล</th>
                          <th className="text-start">รายละเอียด</th>
                        </tr>
                      </thead>
                      <tbody>
                        {!loading && displayRows.length === 0 && (
                          <tr>
                            <td colSpan={5} className="text-center text-muted">
                              ไม่พบข้อมูล
                            </td>
                          </tr>
                        )}

                        {!loading &&
                          displayRows.map((r) => (
                            <tr key={r.brId}>
                              <td className="text-start" data-th="รหัสสาขา">
                                {r.brCode || "-"}
                              </td>
                              <td className="text-start" data-th="ชื่อสาขา">
                                {r.brNameTH || "-"}
                              </td>
                              <td className="text-start" data-th="เบอร์โทร">
                                {r.brMobile || "-"}
                              </td>
                              <td className="text-start" data-th="อีเมล">
                                {r.brEmail || "-"}
                              </td>
                              <td className="text-start" data-th="รายละเอียด">
                                <Button
                                  color="info"
                                  size="sm"
                                  onClick={() => {
                                    sessionStorage.setItem(
                                      "EDIT_BRANCH_ID",
                                      String(r.brId),
                                    );
                                    navigate("/edit-branch", {
                                      state: { brId: r.brId },
                                    });
                                  }}
                                >
                                  ข้อมูล
                                </Button>
                              </td>
                            </tr>
                          ))}
                      </tbody>
                    </Table>
                  </div>
                ) : (
                  <div className="zi-table-wrap">
                    <Table responsive hover className="zi-table mb-0">
                      <thead>
                        <tr>
                          <th className="text-start" style={{ width: 160 }}>
                            รหัสสาขา
                          </th>
                          <th className="text-start" style={{ width: 160 }}>
                            ชื่อสาขา
                          </th>
                          <th className="text-start" style={{ width: 160 }}>
                            เบอร์โทร
                          </th>
                          <th className="text-start" style={{ width: 160 }}>
                            อีเมล
                          </th>
                          <th className="text-end" style={{ width: 160 }}>
                            การจัดการ
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {displayRows.length === 0 ? (
                          <tr>
                            <td
                              colSpan={5}
                              className="text-center text-muted py-4"
                            >
                              ไม่พบข้อมูล
                            </td>
                          </tr>
                        ) : (
                          displayRows.map((r) => (
                            <tr key={r.brId}>
                              <td className="text-start">{r.brCode || "-"}</td>
                              <td className="text-start fw-600">
                                {r.brNameTH || "-"}
                              </td>
                              <td className="text-start">
                                {r.brMobile || "-"}
                              </td>
                              <td className="text-start">{r.brEmail || "-"}</td>
                              <td className="text-end">
                                <Button
                                  color="info"
                                  size="sm"
                                  onClick={() => {
                                    sessionStorage.setItem(
                                      "EDIT_BRANCH_ID",
                                      String(r.brId),
                                    );
                                    navigate("/edit-branch", {
                                      state: { brId: r.brId },
                                    });
                                  }}
                                >
                                  ข้อมูล
                                </Button>
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </Table>
                  </div>
                )}

                {/* Summary */}
                <div className="d-flex justify-content-end justify-content-sm-end justify-content-center mt-2">
                  <div className="text-muted small">
                    แสดง {(page - 1) * pageSize + (displayRows.length ? 1 : 0)}–
                    {Math.min(page * pageSize, filteredTotal)} จากทั้งหมด{" "}
                    {filteredTotal} รายการ
                  </div>
                </div>

                {/* ✅ CSS แบบ Product */}
                <style>{`
                  .zi-table-wrap{
                    background:#fff;
                    border-radius:10px;
                    border:1px solid rgba(0,0,0,.06);
                    overflow:hidden;
                  }
                  .zi-table{ margin:0; }
                  .zi-table thead th{
                    background:#f7fafc;
                    color:#0f172a;
                    font-weight:700;
                    font-size:14px;
                    border-bottom:1px solid rgba(0,0,0,.06);
                    padding:14px 16px;
                    white-space:nowrap;
                  }
                  .zi-table tbody td{
                    padding:14px 16px;
                    border-top:1px solid rgba(0,0,0,.06);
                    vertical-align:middle;
                    font-size:14px;
                    color:#0f172a;
                  }
                  .zi-table tbody tr:hover{ background:#f9fbff; }
                  .fw-600{ font-weight:600; }
                `}</style>
              </CardBody>

              <CardFooter className="py-4">
                <nav aria-label="branch pagination">
                  <Pagination className="pagination justify-content-center mb-0">
                    <PaginationItem disabled={page <= 1}>
                      <PaginationLink
                        onClick={() => setPage(TABLE_KEY, page - 1)}
                      >
                        <i className="fas fa-angle-left" />
                      </PaginationLink>
                    </PaginationItem>

                    {pageItems[0] > 1 && (
                      <>
                        <PaginationItem>
                          <PaginationLink onClick={() => setPage(TABLE_KEY, 1)}>
                            1
                          </PaginationLink>
                        </PaginationItem>
                        {pageItems[0] > 2 && <span className="px-2">…</span>}
                      </>
                    )}

                    {pageItems.map((p) => (
                      <PaginationItem key={p} active={p === page}>
                        <PaginationLink onClick={() => setPage(TABLE_KEY, p)}>
                          {p}
                        </PaginationLink>
                      </PaginationItem>
                    ))}

                    {pageItems[pageItems.length - 1] < totalPages && (
                      <>
                        {pageItems[pageItems.length - 1] < totalPages - 1 && (
                          <span className="px-2">…</span>
                        )}
                        <PaginationItem>
                          <PaginationLink
                            onClick={() => setPage(TABLE_KEY, totalPages)}
                          >
                            {totalPages}
                          </PaginationLink>
                        </PaginationItem>
                      </>
                    )}

                    <PaginationItem disabled={page >= totalPages}>
                      <PaginationLink
                        onClick={() => setPage(TABLE_KEY, page + 1)}
                      >
                        <i className="fas fa-angle-right" />
                      </PaginationLink>
                    </PaginationItem>
                  </Pagination>
                </nav>
              </CardFooter>
            </Card>
          </Col>
        </Row>
      </Container>
    </>
  );
};

export default AdminBranch;
